<template>
  <div class="flex items-center gap-x-1 flex-wrap" style="min-height: 1.5rem">
    <div v-for="(part, index) in formulaParts" :key="index">
      <div v-if="part.type === 'line-break'" class="h-0" style="width: 300px" />
      <BaseText v-else type="heading" size="overline" class="rounded-md text-center w-max mt-1"
      :class="`formula-${getNodeType(part.type, part.value)}`">
        <div v-if="part.type === 'operation'" class="p-2">
          <component :is="operatorMapping[part.value]" :width="12" :height="12" />
        </div>
        <div v-else-if="part.type === 'parentheses'" class="relative px-2 text-center"
        :class="{ 'left-px': part.value === ')' }">
          {{ part.value }}
        </div>
        <span v-else class="py-1 px-2">
          {{ getNodeType(part.type, part.value) === 'integer' ? Number(part.value).toLocaleString() : part.value }}
        </span>
      </BaseText>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

// Icons
import PlusIcon from '../globals/Icons/MathOperators/PlusIcon.vue'
import MinusIcon from '../globals/Icons/MathOperators/MinusIcon.vue'
import MultiplyIcon from '../globals/Icons/MathOperators/MultiplyIcon.vue'
import DivideIcon from '../globals/Icons/MathOperators/DivideIcon.vue'

const MULT_OPS = ['*', '/']
const ADD_OPS = ['+', '-']

export default {
  name: 'MetricFormulaRenderer',
  components: {
    PlusIcon,
    MinusIcon,
    MultiplyIcon,
    DivideIcon
  },
  props: {
    ast: {
      type: Object,
      required: true
    }
  },
  data () {
    const operatorMapping = {
      '/': DivideIcon,
      '*': MultiplyIcon,
      '-': MinusIcon,
      '+': PlusIcon
    }

    return {
      operatorMapping
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    formulaParts () {
      const parts = []
      this.traverseAST(this.ast, parts)
      return parts
    }
  },
  methods: {
    getNodeType (type, val) {
      return !isNaN(val) ? 'integer' : type
    },
    traverseAST (node, parts) {
      if (node.type === 'binOp') {
        if (MULT_OPS.includes(node.operation) && ADD_OPS.includes(node?.left?.operation)) {
          parts.push({ type: 'line-break', value: '' })
          parts.push({ type: 'parentheses', value: '(' })
          this.traverseAST(node.left, parts)
          parts.push({ type: 'parentheses', value: ')' })
          parts.push({ type: 'line-break', value: '' })
        } else {
          this.traverseAST(node.left, parts)
        }

        parts.push({ type: 'operation', value: node.operation })

        if (MULT_OPS.includes(node.operation) && ADD_OPS.includes(node?.right?.operation)) {
          parts.push({ type: 'line-break', value: '' })
          parts.push({ type: 'parentheses', value: '(' })
          this.traverseAST(node.right, parts)
          parts.push({ type: 'parentheses', value: ')' })
          parts.push({ type: 'line-break', value: '' })
        } else {
          this.traverseAST(node.right, parts)
        }
      } else if (node.type === 'literal') {
        // Relative keys are denoted as [relative_space].[metric_key]
        const metricData = `${node.value}`.split('.')
        const key = metricData[metricData.length - 1]
        let metricName = (this.getMetricLookup?.[key]?.name || key).replace(/_/g, ' ')
        if (metricData.length > 1 && metricData[0] === 'relative_total') {
          metricName = `Total ${metricName}`
        }
        parts.push({ type: 'literal', value: metricName })
      }
    }
  }
}
</script>

<style scoped>
.formula-literal {
    color: rgba(60, 148, 249, 1);
    background-color: rgba(60, 148, 249, 0.06);
    width: auto;
    padding: 2px 0;
}

.formula-operation {
    color: rgba(137,113,234,1);
    background-color: rgba(137,113,234,0.06);
    height: auto;
}

.formula-integer {
  color: rgba(39, 176, 139, 1);
  background-color: rgba(39, 176, 139, 0.06);
  padding: 2px 0;
}

.formula-parentheses {
  color: #FBB62D;
  background-color: #FBB62D0F;
  padding: 2px 0;
}
</style>
